import React from 'react'
import { useTranslation } from 'react-i18next'
import { Check, Copy, PlugZap, SearchX } from 'lucide-react'
import { Button } from '@/components/ui/button'

/**
 * Why the app could not start.
 *
 * `notFound` is the only one that is about the address the person typed.
 * `unreachable` and `server` are about a **second** domain they never typed and
 * have no reason to know about — the API — and saying "community not found"
 * for those sends somebody to check a domain that was never the problem. That
 * is what happened to a visitor behind a corporate proxy, who read the title
 * and stopped there.
 */
export interface BootFailure {
  kind: 'notFound' | 'unreachable' | 'server'
  /** The address that did not answer, when there is one to name. */
  url?: string
  /** The HTTP status, when the server answered at all. */
  status?: number
  /** Whatever the browser said, verbatim. */
  detail?: string
}

interface BootErrorScreenProps {
  failure: BootFailure
  /** The host the person is actually on. */
  domain: string
}

/**
 * The screen somebody sees instead of the community.
 *
 * It carries a diagnosis block and a button that copies it, because the person
 * reading this is usually not the person who can fix it: they are on a
 * corporate network, and what gets this solved is a message they can paste to
 * their own IT with the blocked address in it.
 */
export const BootErrorScreen = ({ failure, domain }: BootErrorScreenProps) => {
  const { t } = useTranslation('common')
  const [copied, setCopied] = React.useState(false)

  const isNotFound = failure.kind === 'notFound'
  const Icon = isNotFound ? SearchX : PlugZap

  const diagnostics = [
    `${t('boot.diag.page')}: ${window.location.href}`,
    `${t('boot.diag.domain')}: ${domain}`,
    failure.url ? `${t('boot.diag.blocked')}: ${failure.url}` : null,
    failure.status ? `${t('boot.diag.status')}: ${failure.status}` : null,
    failure.detail ? `${t('boot.diag.detail')}: ${failure.detail}` : null,
    `${t('boot.diag.when')}: ${new Date().toISOString()}`,
    `${t('boot.diag.browser')}: ${navigator.userAgent}`
  ].filter(Boolean).join('\n')

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(diagnostics)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    } catch {
      /* The block is on screen and selectable; a browser that refuses the
         clipboard does not need a second error on top of this one. */
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
      <div className="w-full max-w-lg text-center">
        <div className="w-16 h-16 mx-auto mb-6 bg-red-100 rounded-full flex items-center justify-center">
          <Icon className="w-8 h-8 text-red-600" />
        </div>

        <h1 className="text-2xl font-bold text-gray-900 mb-3">
          {isNotFound ? t('boot.notFound.title') : t('boot.unreachable.title')}
        </h1>

        <p className="text-gray-600 mb-6 leading-relaxed [overflow-wrap:anywhere]">
          {isNotFound
            ? t('boot.notFound.body', { domain })
            : t('boot.unreachable.body', { url: failure.url || t('boot.unreachable.theServer') })}
        </p>

        {/* Only where it is useful: on a network failure there is something a
            person can check, and on an unknown domain there is not. */}
        {!isNotFound && (
          <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 p-4 text-left">
            <p className="text-sm font-medium text-amber-900">{t('boot.unreachable.whatToDo')}</p>
            <ul className="mt-2 space-y-1.5 text-sm leading-relaxed text-amber-800 [overflow-wrap:anywhere]">
              <li>· {t('boot.unreachable.step1', { url: failure.url })}</li>
              <li>· {t('boot.unreachable.step2')}</li>
              <li>· {t('boot.unreachable.step3')}</li>
            </ul>
          </div>
        )}

        <details className="mb-6 text-left">
          <summary className="cursor-pointer text-sm text-gray-500 hover:text-gray-700">
            {t('boot.diag.title')}
          </summary>
          <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap break-all rounded-lg bg-gray-100 p-3 text-xs leading-relaxed text-gray-700">
            {diagnostics}
          </pre>
          <button
            type="button"
            onClick={copy}
            className="mt-2 inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline"
          >
            {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            {copied ? t('boot.diag.copied') : t('boot.diag.copy')}
          </button>
        </details>

        <Button onClick={() => window.location.reload()} className="bg-primary text-white hover:bg-primary/90">
          {t('retry')}
        </Button>
      </div>
    </div>
  )
}
